import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ShipmentCostBreakdownRowView } from '../../api/generated/models/ShipmentCostBreakdownRowView';
import type { ShipmentPerProductRowView } from '../../api/generated/models/ShipmentPerProductRowView';

interface Props {
  /** Component rows without the "total" row; drives one allocated column per component. */
  componentRows: ShipmentCostBreakdownRowView[];
  products: ShipmentPerProductRowView[];
  /** Totals row from the API — reconciles to the container table to the cent (AC-10). */
  totals: ShipmentPerProductRowView;
  componentLabel: (row: ShipmentCostBreakdownRowView) => string;
  /** Container basis; a column only names its own basis when it differs from this. */
  allocationMethod: string;
  methodLabel: (method: string) => string;
  sym: string;
}

const dash = (v?: string | null) => (v != null && v !== '' ? v : '-');

/** Per-product landed cost table (FR-6). */
const ShipmentPerProductTable: React.FC<Props> = ({
  componentRows,
  products,
  totals,
  componentLabel,
  allocationMethod,
  methodLabel,
  sym,
}) => {
  const { t } = useTranslation();

  const allocationOf = (row: ShipmentPerProductRowView, key: string) =>
    (row.allocations ?? []).find((a) => a.key === key);

  return (
    <div className="per-product-table-scroll">
      <table className="per-product-table">
        <thead>
          <tr>
            <th>{t('per_product_product')}</th>
            <th>{t('per_product_qty_cartons')}</th>
            <th>{t('per_product_unit_price')} ({sym})</th>
            {componentRows.map((row) => (
              <th key={row.key}>
                {componentLabel(row)} ({sym})
                {row.basis && row.basis !== allocationMethod && (
                  <div className="per-product-table__basis">{methodLabel(row.basis)}</div>
                )}
              </th>
            ))}
            <th>{t('per_product_landed_planned')} ({sym})</th>
            <th>{t('per_product_landed_effective')} ({sym})</th>
            <th>{t('per_product_landed_per_unit')} ({sym})</th>
            <th>{t('per_product_landed_per_carton')} ({sym})</th>
            <th>{t('per_product_variance')} ({sym})</th>
            <th>{t('per_product_pct_of_container')}</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr key={product.itemId ?? product.sku ?? ''}>
              <td>
                <div className="per-product-table__name">{product.productName ?? '-'}</div>
                <div className="per-product-table__sku">{product.sku ?? ''}</div>
              </td>
              <td>
                {product.units ?? '-'} / {product.cartons ?? '-'}
              </td>
              <td>{dash(product.unitPrice)}</td>
              {componentRows.map((row) => (
                <td key={row.key}>{dash(allocationOf(product, row.key)?.effective)}</td>
              ))}
              <td>{product.landedPlanned}</td>
              <td>{product.landedEffective}</td>
              <td>
                {dash(product.landedPerUnitEffective)}
                <span className="per-product-table__planned">
                  {product.landedPerUnitPlanned != null
                    ? ` (${t('per_product_planned_short')} ${product.landedPerUnitPlanned})`
                    : ''}
                </span>
              </td>
              <td>
                {dash(product.landedPerCartonEffective)}
                <span className="per-product-table__planned">
                  {product.landedPerCartonPlanned != null
                    ? ` (${t('per_product_planned_short')} ${product.landedPerCartonPlanned})`
                    : ''}
                </span>
              </td>
              <td className={product.variance.startsWith('-') ? 'text-danger' : ''}>
                {product.variance}
              </td>
              <td>{product.pctOfContainer != null ? `${product.pctOfContainer}%` : '-'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="per-product-table__total">
            <td>
              <strong>{t('cost_total')}</strong>
            </td>
            <td>
              <strong>
                {totals.units ?? '-'} / {totals.cartons ?? '-'}
              </strong>
            </td>
            <td>-</td>
            {componentRows.map((row) => (
              <td key={row.key}>
                <strong>{dash(allocationOf(totals, row.key)?.effective)}</strong>
              </td>
            ))}
            <td>
              <strong>{totals.landedPlanned}</strong>
            </td>
            <td>
              <strong>{totals.landedEffective}</strong>
            </td>
            <td>
              <strong>{dash(totals.landedPerUnitEffective)}</strong>
            </td>
            <td>
              <strong>{dash(totals.landedPerCartonEffective)}</strong>
            </td>
            <td className={totals.variance.startsWith('-') ? 'text-danger' : ''}>
              <strong>{totals.variance}</strong>
            </td>
            <td>
              <strong>{totals.pctOfContainer != null ? `${totals.pctOfContainer}%` : '-'}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default ShipmentPerProductTable;
